/**
 * @module i18nStore
 * @description Internationalization store for Preparo
 *
 * Provides translations for the public interface in English and French.
 * The language preference is persisted to localStorage.
 */

import { writable, derived, type Readable } from "svelte/store";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Supported language codes */
export type LanguageCode = "en" | "fr";

/** Language info */
export interface LanguageInfo {
  code: LanguageCode;
  label: string;
  flag: string;
}

/** Languages configuration */
export type LanguagesConfig = Record<LanguageCode, LanguageInfo>;

/** Translation key type - all possible keys */
export type TranslationKey = string;

/** Translations for a single language */
export type LanguageTranslations = Record<TranslationKey, string>;

/** All translations */
export type TranslationsConfig = Record<LanguageCode, LanguageTranslations>;

/** Translation parameters */
export interface TranslationParams {
  count?: number;
  [key: string]: string | number | undefined;
}

/** Translation function type */
export type TranslationFunction = (
  key: TranslationKey,
  params?: TranslationParams,
) => string;

/** Language store interface */
export interface LanguageStore extends Readable<LanguageCode> {
  set: (lang: LanguageCode) => void;
  toggle: () => void;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Available languages */
export const LANGUAGES: LanguagesConfig = {
  en: { code: "en", label: "English", flag: "🇬🇧" },
  fr: { code: "fr", label: "Français", flag: "🇫🇷" },
};

/** Translations object */
export const translations: TranslationsConfig = {
  en: {
    // App Header
    "app.hidePanel": "Hide panels",
    "app.showPanels": "Show panels",
    "app.menu": "Menu",
    "app.theme": "Theme",
    "app.theme.light": "Light",
    "app.theme.dark": "Dark",
    "app.theme.system": "System",
    "app.language": "Language",
    "app.loading": "Loading...",
    "app.syncing": "Syncing...",
    "app.initializing": "Initializing Preparo...",
    "app.databaseError": "Database Error",
    "app.databaseErrorMessage": "Failed to initialize the database:",
    "app.reload": "Reload",

    // Sync Indicator
    "sync.syncing": "Syncing...",
    "sync.idle": "Sync enabled",
    "sync.error": "Sync error",
    "sync.offline": "Offline",
    "sync.offlinePending": "Offline — {count} pending",
    "sync.pending": "{count} pending",
    "sync.reconnecting": "Reconnecting — syncing changes...",
    "sync.lastSynced": "Synced",
    "sync.retry": "Retry now",
    // Conflict Resolution
    "sync.conflictsDetected": "Sync conflicts detected",
    "sync.conflictTitle": "Sync Conflict",
    "sync.localVersion": "Local version",
    "sync.remoteVersion": "Remote version (Git)",
    "sync.keepLocal": "Keep my version",
    "sync.useRemote": "Use remote version",
    "sync.manualMerge": "Merge manually",
    "sync.applyMerge": "Apply merge",
    "sync.showDiff": "Show diff",
    "sync.otherConflicts": "Other conflicts",

    // Projects Panel
    "projects.title": "Projects",
    "projects.newProject": "New project",
    "projects.searchPlaceholder": "Search projects...",
    "projects.projectTitle": "Project title",
    "projects.projectType": "Type",
    "projects.typeBook": "Book",
    "projects.typeArticle": "Article",
    "projects.create": "Create",
    "projects.cancel": "Cancel",
    "projects.loading": "Loading projects...",
    "projects.retry": "Retry",
    "projects.noProjectsFound": 'No projects found for "{query}"',
    "projects.noProjectsYet": "No projects yet",
    "projects.createFirst": "Create your first project",
    "projects.docCount": "{count} doc | {count} docs",
    "projects.edit": "Edit",
    "projects.delete": "Delete",
    "projects.editPrompt": "New project title:",
    "projects.deleteConfirm": 'Delete project "{title}"?',
    "projects.menu": "Menu",

    // Presets
    "presets.title": "Presets",
    "presets.showPresets": "Presets",
    "presets.backToProjects": "Projects",
    "presets.newPreset": "New preset",
    "presets.presetName": "Preset name",
    "presets.create": "Create",
    "presets.cancel": "Cancel",
    "presets.noPresetsYet": "No presets yet",
    "presets.createFirst": "Create your first preset",
    "presets.searchPlaceholder": "Search presets...",
    "presets.edit": "Edit",
    "presets.rename": "Rename",
    "presets.delete": "Delete",
    "presets.renamePrompt": "New preset name:",
    "presets.deleteConfirm": 'Delete preset "{name}"?',
    "presets.import": "Import",
    "presets.export": "Export",
    "presets.importing": "Importing...",
    "presets.importSuccess": "Preset imported successfully",
    "presets.importError": "Failed to import preset",
    "presets.exportError": "Failed to export preset",

    // Docs Panel
    "docs.title": "Documents",
    "docs.newDoc": "New document",
    "docs.import": "Import",
    "docs.importDocuments": "Import documents",
    "docs.importZipProject": "Import project (ZIP)",
    "docs.importZipSuccess": "Project \"{title}\" imported successfully",
    "docs.importZipError": "ZIP import failed: {message}",
    "docs.searchPlaceholder": "Search documents...",
    "docs.docTitle": "Document title",
    "docs.create": "Create",
    "docs.cancel": "Cancel",
    "docs.loading": "Loading documents...",
    "docs.retry": "Retry",
    "docs.noDocsFound": "No documents found",
    "docs.noDocsYet": "No documents yet",
    "docs.createFirst": "Create your first document",
    "docs.selectProjectFirst": "Select a project first",
    "docs.edit": "Edit",
    "docs.delete": "Delete",
    "docs.deleteMultiple": "Delete {count} documents",
    "docs.moveUp": "Move up",
    "docs.moveDown": "Move down",
    "docs.editPrompt": "New doc title:",
    "docs.deleteConfirm": 'Delete document "{title}"?',
    "docs.deleteMultipleConfirm": "Delete {count} selected documents?",
    "docs.statusTodo": "Todo",
    "docs.statusInProgress": "In progress",
    "docs.statusDone": "Done",
    "docs.statusArchived": "Archived",
    "docs.filterAll": "All",
    "docs.words": "{count} word | {count} words",
    "docs.chars": "{count} char | {count} chars",
    "docs.filename": "File: {name}",
    "docs.importSuccess": "{count} document(s) imported successfully",
    "docs.importPartial":
      "{success} document(s) imported out of {total}. Some documents could not be imported.",
    "docs.importError": "Error importing documents",
    "docs.export": "Export project",
    "docs.exportEpub3": "Export EPUB 3",
    "docs.exportEpub2": "Export EPUB 2",
    "docs.exportZip": "Export to InDesign",
    "docs.exportDocx": "Export Word (.docx)",
    "docs.exportOdt": "Export ODT",
    "docs.exportPdf": "Export PDF",
    "docs.exportHtml": "Export HTML",
    "docs.noDocsToExport":
      "No documents to export. Please assign a document type to at least one document.",
    "docs.noContentToExport":
      "No content to export. Please add content to at least one document.",
    "docs.exportError": "Export failed: {message}",
    "docs.pleaseSelectProject": "Please select a project first",
    "docs.pleaseSelectProjectImport":
      "Please select a project before importing a document",
    "docs.tabText": "Texts",
    "docs.tabImages": "Images",
    "docs.tabTemplates": "Templates",

    // Images
    "images.upload": "Upload image",
    "images.empty": "No images yet",
    "images.emptyHint": "Upload images to use in your documents",
    "images.delete": "Delete",
    "images.copyMarkdown": "Copy Markdown",
    "images.confirmDelete": "Delete this image?",
    "images.fileTooLarge": "File too large (max 10MB)",
    "images.uploadError": "Error uploading image",
    "images.deleteError": "Error deleting image",
    "images.copied": "Markdown copied to clipboard",

    // Templates
    "templates.upload": "Upload template",
    "templates.empty": "No templates yet",
    "templates.emptyHint":
      "Upload CSS, font, or Lua filter files for EPUB export customization",
    "templates.delete": "Delete",
    "templates.confirmDelete": "Delete this template?",
    "templates.fileTooLarge": "File too large (max 5MB)",
    "templates.uploadError": "Error uploading template",
    "templates.deleteError": "Error deleting template",

    // Lua Filters
    "luafilters.upload": "Upload Lua filter",
    "luafilters.empty": "No Lua filters yet",
    "luafilters.emptyHint": "Upload .lua files for Pandoc custom processing",

    // CSL (Citation Style Language)
    "templates.cslTitle": "Bibliography Style (CSL)",
    "templates.cslDescription": "Citation Style Language files for bibliography formatting",
    "templates.cslEmpty": "No CSL files yet",
    "templates.cslEmptyHint": "Upload .csl files to customize bibliography formatting",
    "templates.cslUpload": "Upload CSL file",

    // Image Picker (toolbar)
    "imagePicker.gallery": "Gallery",
    "imagePicker.url": "URL",
    "imagePicker.noImages": "No images in this project",
    "imagePicker.noImagesHint": "Upload images in the Images tab first",
    "imagePicker.urlLabel": "Image URL",
    "imagePicker.altLabel": "Alt text (optional)",
    "imagePicker.altPlaceholder": "Image description",
    "imagePicker.insert": "Insert image",
    "imagePicker.back": "Back",

    // Docs Panel - Sections
    "docs.section.undefined": "Undefined type",
    "docs.section.undefinedDesc": "Choose a document type to classify",
    "docs.section.frontmatter": "Frontmatter",
    "docs.section.frontmatterDesc": "Preface, introduction, foreword...",
    "docs.section.bodymatter": "Bodymatter",
    "docs.section.bodymatterDesc": "Main chapters",
    "docs.section.backmatter": "Backmatter",
    "docs.section.backmatterDesc": "Appendices, bibliography, index...",

    // Notes Panel
    "notes.title": "Notes",
    "notes.documentTitle": "Document title",
    "notes.clickToEdit": "Click to edit",
    "notes.loading": "Loading notes...",
    "notes.selectDoc": "Select a doc to view and edit notes",
    "notes.error": "Error",
    "notes.saving": "Saving...",
    "notes.unsavedChanges": "Unsaved changes",
    "notes.saved": "Saved",
    "notes.documentType": "Document type",
    "notes.export": "Export document",
    "notes.exporting": "Exporting...",
    "notes.exportError": "Export error: {message}",
    "notes.fullscreen": "Toggle fullscreen",
    "notes.toggleSource": "Toggle Markdown source view",
    "notes.toggleNavigator": "Toggle Navigator panel",
    "notes.navigator": "Navigator",
    "notes.closeNavigator": "Close Navigator",
    "notes.navTabToc": "Table of Contents",
    "notes.navTabStyle": "Style",
    "notes.navSubTabParagraph": "Paragraph",
    "notes.navSubTabCharacter": "Character",
    "notes.navTabPlaceholders": "Placeholders",
    "notes.tocEmpty":
      "No headings found. Add headings (# to ####) to see them here.",
    "notes.blocksEmpty":
      "No custom blocks found. Use :::name to create blocks.",
    "notes.stylesEmpty":
      "No inline styles found. Use [text]{.style} to add styles.",
    "notes.placeholdersEmpty":
      "No placeholders available. Set project metadata in Project Configuration.",
    "notes.placeholderInserted": "Placeholder inserted",
    "notes.sourceMarkdown": "Source Markdown",
    "notes.sourcePlaceholder": "Markdown source code...",
    "notes.editorPlaceholder": "Start writing your notes...",
    "notes.shorttitle": "Short title",
    "notes.shorttitlePlaceholder": "Short title...",
    "notes.clickToEditShorttitle": "Click to edit short title",
    "notes.errorUpdatingTitle": "Error updating title",
    "notes.errorUpdatingType": "Error updating document type",

    // Notes Panel - Split document
    "notes.split": "Split document by H1",
    "notes.splitConfirm": "Split this document into {count} documents (one per H1 heading)? The original document will be deleted.",
    "notes.splitSuccess": "{count} documents created, original document deleted",
    "notes.splitNoH1": "No H1 headings found. Add H1 headings (# Title) to split the document.",
    "notes.splitError": "Error splitting document: {message}",

    // Notes Panel - Export formats
    "notes.export.markdown": "Markdown (.md)",
    "notes.export.word": "Word (.docx)",
    "notes.export.odt": "OpenDocument (.odt)",
    "notes.export.pdf": "PDF (.pdf)",
    "notes.export.html": "HTML (.html)",

    // Notes Panel - Document types
    "docType.chapter": "Chapter",
    "docType.part": "Part",
    "docType.titlepage": "Title Page",
    "docType.copyright-page": "Copyright Page",
    "docType.other-credits": "Other Credits",
    "docType.prologue": "Prologue",
    "docType.abstract": "Abstract",
    "docType.acknowledgments": "Acknowledgments",
    "docType.dedication": "Dedication",
    "docType.credits": "Credits",
    "docType.keywords": "Keywords",
    "docType.imprint": "Imprint",
    "docType.contributors": "Contributors",
    "docType.errata": "Errata",
    "docType.revision-history": "Revision History",
    "docType.halftitlepage": "Half Title Page",
    "docType.seriespage": "Series Page",
    "docType.foreword": "Foreword",
    "docType.preface": "Preface",
    "docType.frontispiece": "Frontispiece",
    "docType.introduction": "Introduction",
    "docType.appendix": "Appendix",
    "docType.colophon": "Colophon",
    "docType.bibliography": "Bibliography",
    "docType.index": "Index",
    "docType.conclusion": "Conclusion",
    "docType.annexe": "Annexe",
    "docType.postface": "Postface",

    // Document Importer
    "importer.title": "Import Document",
    "importer.close": "Close",
    "importer.dropzone": "Drag and drop files here",
    "importer.or": "or",
    "importer.browse": "Browse files",
    "importer.supportedFormats":
      "Supported formats: Word (.docx), PDF, HTML, EPUB, ODT, RTF, Markdown",
    "importer.converting": "Converting...",
    "importer.import": "Import",
    "importer.cancel": "Cancel",

    // Common
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.loading": "Loading...",
    "common.error": "Error",
    "common.success": "Success",
    "common.confirm": "Confirm",
    "common.yes": "Yes",
    "common.no": "No",

    // Error Boundary
    "errors.componentError": "Something went wrong",
    "errors.componentErrorMessage":
      "An error occurred in the {component} component. You can try again or reload the page.",
    "errors.technicalDetails": "Technical details",
    "errors.retry": "Try again",
    "errors.attemptsLeft": "attempts left",
    "errors.maxRetriesReached":
      "Maximum retry attempts reached. Please reload the page.",

    // Validation errors
    "validation.required": "{field} is required",
    "validation.minLength": "{field} must be at least {min} characters",
    "validation.maxLength": "{field} must be at most {max} characters",
    "validation.invalidFormat": "{field} has an invalid format",
    "validation.invalidId": "Invalid identifier format",
    "validation.invalidDate": "Invalid date format",
    "validation.invalidStatus": "Invalid status value",
    "validation.invalidDocumentType": "Invalid document type",
    "validation.invalidDocumentSection": "Invalid document section",
    "validation.fileTooLarge": "File is too large (max {max})",
    "validation.unsupportedFormat": "Unsupported file format",
    "validation.unsupportedMimeType": "Unsupported file type",

    // Network errors
    "network.timeout":
      "Request timed out. Please check your connection and try again.",
    "network.offline":
      "You appear to be offline. Please check your connection.",
    "network.serverError": "Server error. Please try again later.",
    "network.connectionFailed": "Could not connect to the server",

    // Authentication
    "auth.checking": "Checking authentication...",
    "auth.loginRequired": "Login Required",
    "auth.redirecting": "Redirecting to Spectral Cloud Portal...",
    "auth.loginWithPortal": "Login with Portal",

    // Navigator - Bibliography Tab
    "notes.navTabBibliography": "Bibliography",

    // Bibliography Panel
    "bibliography.notConfigured": "Zotero is not configured",
    "bibliography.configureInPortal": "Configure in Portal",
    "bibliography.loading": "Loading Zotero...",
    "bibliography.initializing": "Initializing...",
    "bibliography.retry": "Retry",
    "bibliography.myLibrary": "My Library",
    "bibliography.collections": "Collections",
    "bibliography.noCollections": "No collections",
    "bibliography.items": "Items",
    "bibliography.noItems": "No items in this collection",
    "bibliography.untitled": "Untitled",
    "bibliography.doubleClickToInsert": "Double-click to insert citation",
    "bibliography.tabLocal": "Local",
    "bibliography.tabZotero": "Zotero",
    "bibliography.localEmpty": "No local references yet",
    "bibliography.localEmptyHint": "Insert citations from Zotero to save them locally",
    "bibliography.lastSync": "Last sync",
    "bibliography.saveCollection": "Save collection",
    "bibliography.syncingMissing": "Syncing citations...",
  },

  fr: {
    // App Header
    "app.hidePanel": "Masquer les panneaux",
    "app.showPanels": "Afficher les panneaux",
    "app.menu": "Menu",
    "app.theme": "Thème",
    "app.theme.light": "Clair",
    "app.theme.dark": "Sombre",
    "app.theme.system": "Système",
    "app.language": "Langue",
    "app.loading": "Chargement...",
    "app.syncing": "Synchronisation...",
    "app.initializing": "Initialisation de Preparo...",
    "app.databaseError": "Erreur de base de données",
    "app.databaseErrorMessage":
      "Échec de l'initialisation de la base de données :",
    "app.reload": "Recharger",

    // Sync Indicator
    "sync.syncing": "Synchronisation...",
    "sync.idle": "Synchronisation activée",
    "sync.error": "Erreur de synchronisation",
    "sync.offline": "Hors ligne",
    "sync.offlinePending": "Hors ligne — {count} en attente",
    "sync.pending": "{count} en attente",
    "sync.reconnecting": "Reconnexion — synchronisation en cours...",
    "sync.lastSynced": "Synchronisé",
    "sync.retry": "Réessayer",
    // Résolution de conflits
    "sync.conflictsDetected": "Conflits de synchronisation détectés",
    "sync.conflictTitle": "Conflit de synchronisation",
    "sync.localVersion": "Version locale",
    "sync.remoteVersion": "Version distante (Git)",
    "sync.keepLocal": "Garder ma version",
    "sync.useRemote": "Utiliser la version distante",
    "sync.manualMerge": "Fusionner manuellement",
    "sync.applyMerge": "Appliquer la fusion",
    "sync.showDiff": "Afficher le diff",
    "sync.otherConflicts": "Autres conflits",

    // Projects Panel
    "projects.title": "Projets",
    "projects.newProject": "Nouveau projet",
    "projects.searchPlaceholder": "Rechercher des projets...",
    "projects.projectTitle": "Titre du projet",
    "projects.projectType": "Type",
    "projects.typeBook": "Livre",
    "projects.typeArticle": "Article",
    "projects.create": "Créer",
    "projects.cancel": "Annuler",
    "projects.loading": "Chargement des projets...",
    "projects.retry": "Réessayer",
    "projects.noProjectsFound": "Aucun projet trouvé pour « {query} »",
    "projects.noProjectsYet": "Aucun projet pour le moment",
    "projects.createFirst": "Créez votre premier projet",
    "projects.docCount": "{count} doc | {count} docs",
    "projects.edit": "Modifier",
    "projects.delete": "Supprimer",
    "projects.editPrompt": "Nouveau titre du projet :",
    "projects.deleteConfirm": "Supprimer le projet « {title} » ?",
    "projects.menu": "Menu",

    // Presets
    "presets.title": "Presets",
    "presets.showPresets": "Presets",
    "presets.backToProjects": "Projets",
    "presets.newPreset": "Nouveau preset",
    "presets.presetName": "Nom du preset",
    "presets.create": "Créer",
    "presets.cancel": "Annuler",
    "presets.noPresetsYet": "Aucun preset pour le moment",
    "presets.createFirst": "Créez votre premier preset",
    "presets.searchPlaceholder": "Rechercher des presets...",
    "presets.edit": "Modifier",
    "presets.rename": "Renommer",
    "presets.delete": "Supprimer",
    "presets.renamePrompt": "Nouveau nom du preset :",
    "presets.deleteConfirm": "Supprimer le preset « {name} » ?",
    "presets.import": "Importer",
    "presets.export": "Exporter",
    "presets.importing": "Import en cours...",
    "presets.importSuccess": "Preset importe avec succes",
    "presets.importError": "Erreur lors de l'import du preset",
    "presets.exportError": "Erreur lors de l'export du preset",

    // Docs Panel
    "docs.title": "Documents",
    "docs.newDoc": "Nouveau document",
    "docs.import": "Importer",
    "docs.importDocuments": "Importer des documents",
    "docs.importZipProject": "Importer un projet (ZIP)",
    "docs.importZipSuccess": "Projet \"{title}\" importé avec succès",
    "docs.importZipError": "Échec de l'import ZIP : {message}",
    "docs.searchPlaceholder": "Rechercher des documents...",
    "docs.docTitle": "Titre du document",
    "docs.create": "Créer",
    "docs.cancel": "Annuler",
    "docs.loading": "Chargement des documents...",
    "docs.retry": "Réessayer",
    "docs.noDocsFound": "Aucun document trouvé",
    "docs.noDocsYet": "Aucun document pour le moment",
    "docs.createFirst": "Créez votre premier document",
    "docs.selectProjectFirst": "Sélectionnez d'abord un projet",
    "docs.edit": "Modifier",
    "docs.delete": "Supprimer",
    "docs.deleteMultiple": "Supprimer {count} documents",
    "docs.moveUp": "Monter",
    "docs.moveDown": "Descendre",
    "docs.editPrompt": "Nouveau titre du document :",
    "docs.deleteConfirm": "Supprimer le document « {title} » ?",
    "docs.deleteMultipleConfirm": "Supprimer les {count} documents sélectionnés ?",
    "docs.statusTodo": "À faire",
    "docs.statusInProgress": "En cours",
    "docs.statusDone": "Terminé",
    "docs.statusArchived": "Archivé",
    "docs.filterAll": "Tous",
    "docs.words": "{count} mot | {count} mots",
    "docs.chars": "{count} car. | {count} car.",
    "docs.filename": "Fichier : {name}",
    "docs.importSuccess": "{count} document(s) importé(s) avec succès",
    "docs.importPartial":
      "{success} document(s) importé(s) sur {total}. Certains documents n'ont pas pu être importés.",
    "docs.importError": "Erreur lors de l'import des documents",
    "docs.export": "Exporter le projet",
    "docs.exportEpub3": "Exporter EPUB 3",
    "docs.exportEpub2": "Exporter EPUB 2",
    "docs.exportZip": "Exporter vers InDesign",
    "docs.exportDocx": "Exporter Word (.docx)",
    "docs.exportOdt": "Exporter ODT",
    "docs.exportPdf": "Exporter PDF",
    "docs.exportHtml": "Exporter HTML",
    "docs.noDocsToExport":
      "Aucun document à exporter. Veuillez attribuer un type de document à au moins un document.",
    "docs.noContentToExport":
      "Aucun contenu à exporter. Veuillez ajouter du contenu à au moins un document.",
    "docs.exportError": "Erreur d'export : {message}",
    "docs.pleaseSelectProject": "Veuillez d'abord sélectionner un projet",
    "docs.pleaseSelectProjectImport":
      "Veuillez sélectionner un projet avant d'importer un document",
    "docs.tabText": "Textes",
    "docs.tabImages": "Images",
    "docs.tabTemplates": "Modèles",

    // Images
    "images.upload": "Téléverser une image",
    "images.empty": "Aucune image pour le moment",
    "images.emptyHint":
      "Téléversez des images pour les utiliser dans vos documents",
    "images.delete": "Supprimer",
    "images.copyMarkdown": "Copier le Markdown",
    "images.confirmDelete": "Supprimer cette image ?",
    "images.fileTooLarge": "Fichier trop volumineux (max 10 Mo)",
    "images.uploadError": "Erreur lors du téléversement de l'image",
    "images.deleteError": "Erreur lors de la suppression de l'image",
    "images.copied": "Markdown copié dans le presse-papiers",

    // Templates
    "templates.upload": "Téléverser un modèle",
    "templates.empty": "Aucun modèle pour le moment",
    "templates.emptyHint":
      "Téléversez des fichiers CSS, de polices ou des filtres Lua pour personnaliser l'export EPUB",
    "templates.delete": "Supprimer",
    "templates.confirmDelete": "Supprimer ce modèle ?",
    "templates.fileTooLarge": "Fichier trop volumineux (max 5 Mo)",
    "templates.uploadError": "Erreur lors du téléversement du modèle",
    "templates.deleteError": "Erreur lors de la suppression du modèle",

    // Lua Filters
    "luafilters.upload": "Téléverser un filtre Lua",
    "luafilters.empty": "Aucun filtre Lua pour le moment",
    "luafilters.emptyHint": "Téléversez des fichiers .lua pour le traitement Pandoc personnalisé",

    // CSL (Citation Style Language)
    "templates.cslTitle": "Style bibliographique (CSL)",
    "templates.cslDescription": "Fichiers Citation Style Language pour le formatage de la bibliographie",
    "templates.cslEmpty": "Aucun fichier CSL pour le moment",
    "templates.cslEmptyHint": "Téléversez des fichiers .csl pour personnaliser le formatage de la bibliographie",
    "templates.cslUpload": "Téléverser un fichier CSL",

    // Image Picker (toolbar)
    "imagePicker.gallery": "Galerie",
    "imagePicker.url": "URL",
    "imagePicker.noImages": "Aucune image dans ce projet",
    "imagePicker.noImagesHint": "Téléversez des images dans l'onglet Images",
    "imagePicker.urlLabel": "URL de l'image",
    "imagePicker.altLabel": "Texte alternatif (optionnel)",
    "imagePicker.altPlaceholder": "Description de l'image",
    "imagePicker.insert": "Insérer l'image",
    "imagePicker.back": "Retour",

    // Docs Panel - Sections
    "docs.section.undefined": "Type non défini",
    "docs.section.undefinedDesc":
      "Choisissez un type de document pour le classer",
    "docs.section.frontmatter": "Liminaires",
    "docs.section.frontmatterDesc": "Préface, introduction, avant-propos...",
    "docs.section.bodymatter": "Corps du texte",
    "docs.section.bodymatterDesc": "Chapitres principaux",
    "docs.section.backmatter": "Annexes",
    "docs.section.backmatterDesc": "Appendices, bibliographie, index...",

    // Notes Panel
    "notes.title": "Notes",
    "notes.documentTitle": "Titre du document",
    "notes.clickToEdit": "Cliquez pour modifier",
    "notes.loading": "Chargement des notes...",
    "notes.selectDoc":
      "Sélectionnez un document pour voir et modifier les notes",
    "notes.error": "Erreur",
    "notes.saving": "Enregistrement...",
    "notes.unsavedChanges": "Modifications non enregistrées",
    "notes.saved": "Enregistré",
    "notes.documentType": "Type de document",
    "notes.export": "Exporter le document",
    "notes.exporting": "Export en cours...",
    "notes.exportError": "Erreur d'export : {message}",
    "notes.fullscreen": "Plein écran",
    "notes.toggleSource": "Afficher le code source Markdown",
    "notes.toggleNavigator": "Afficher le panneau Navigateur",
    "notes.navigator": "Navigateur",
    "notes.closeNavigator": "Fermer le Navigateur",
    "notes.navTabToc": "Table des matières",
    "notes.navTabStyle": "Style",
    "notes.navSubTabParagraph": "Paragraphe",
    "notes.navSubTabCharacter": "Caractère",
    "notes.navTabPlaceholders": "Placeholders",
    "notes.tocEmpty":
      "Aucun titre trouvé. Ajoutez des titres (# à ####) pour les voir ici.",
    "notes.blocksEmpty":
      "Aucun bloc personnalisé. Utilisez :::nom pour créer des blocs.",
    "notes.stylesEmpty":
      "Aucun style inline. Utilisez [texte]{.style} pour ajouter des styles.",
    "notes.placeholdersEmpty":
      "Aucun placeholder disponible. Configurez les métadonnées dans Configuration du projet.",
    "notes.placeholderInserted": "Placeholder inséré",
    "notes.sourceMarkdown": "Source Markdown",
    "notes.sourcePlaceholder": "Code source Markdown...",
    "notes.editorPlaceholder": "Commencez à écrire vos notes...",
    "notes.shorttitle": "Titre court",
    "notes.shorttitlePlaceholder": "Titre court...",
    "notes.clickToEditShorttitle": "Cliquez pour modifier le titre court",
    "notes.errorUpdatingTitle": "Erreur lors de la mise à jour du titre",
    "notes.errorUpdatingType":
      "Erreur lors de la mise à jour du type de document",

    // Notes Panel - Split document
    "notes.split": "Diviser le document par H1",
    "notes.splitConfirm": "Diviser ce document en {count} documents (un par titre H1) ? Le document original sera supprimé.",
    "notes.splitSuccess": "{count} documents créés, document original supprimé",
    "notes.splitNoH1": "Aucun titre H1 trouvé. Ajoutez des titres H1 (# Titre) pour diviser le document.",
    "notes.splitError": "Erreur lors de la division : {message}",

    // Notes Panel - Export formats
    "notes.export.markdown": "Markdown (.md)",
    "notes.export.word": "Word (.docx)",
    "notes.export.odt": "OpenDocument (.odt)",
    "notes.export.pdf": "PDF (.pdf)",
    "notes.export.html": "HTML (.html)",

    // Notes Panel - Document types
    "docType.chapter": "Chapitre",
    "docType.part": "Partie",
    "docType.titlepage": "Page de titre",
    "docType.copyright-page": "Page de copyright",
    "docType.other-credits": "Autres crédits",
    "docType.prologue": "Prologue",
    "docType.abstract": "Résumé",
    "docType.acknowledgments": "Remerciements",
    "docType.dedication": "Dédicace",
    "docType.credits": "Crédits",
    "docType.keywords": "Mots-clés",
    "docType.imprint": "Achevé d'imprimer",
    "docType.contributors": "Contributeurs",
    "docType.errata": "Errata",
    "docType.revision-history": "Historique des révisions",
    "docType.halftitlepage": "Faux-titre",
    "docType.seriespage": "Page de collection",
    "docType.foreword": "Avant-propos",
    "docType.preface": "Préface",
    "docType.frontispiece": "Frontispice",
    "docType.introduction": "Introduction",
    "docType.appendix": "Appendice",
    "docType.colophon": "Colophon",
    "docType.bibliography": "Bibliographie",
    "docType.index": "Index",
    "docType.conclusion": "Conclusion",
    "docType.annexe": "Annexe",
    "docType.postface": "Postface",

    // Document Importer
    "importer.title": "Importer un document",
    "importer.close": "Fermer",
    "importer.dropzone": "Glissez-déposez vos fichiers ici",
    "importer.or": "ou",
    "importer.browse": "Parcourir les fichiers",
    "importer.supportedFormats":
      "Formats supportés : Word (.docx), PDF, HTML, EPUB, ODT, RTF, Markdown",
    "importer.converting": "Conversion en cours...",
    "importer.import": "Importer",
    "importer.cancel": "Annuler",

    // Common
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.delete": "Supprimer",
    "common.edit": "Modifier",
    "common.close": "Fermer",
    "common.loading": "Chargement...",
    "common.error": "Erreur",
    "common.success": "Succès",
    "common.confirm": "Confirmer",
    "common.yes": "Oui",
    "common.no": "Non",

    // Error Boundary
    "errors.componentError": "Une erreur est survenue",
    "errors.componentErrorMessage":
      "Une erreur s'est produite dans le composant {component}. Vous pouvez réessayer ou recharger la page.",
    "errors.technicalDetails": "Détails techniques",
    "errors.retry": "Réessayer",
    "errors.attemptsLeft": "tentatives restantes",
    "errors.maxRetriesReached":
      "Nombre maximum de tentatives atteint. Veuillez recharger la page.",

    // Validation errors
    "validation.required": "{field} est requis",
    "validation.minLength": "{field} doit contenir au moins {min} caractères",
    "validation.maxLength": "{field} doit contenir au maximum {max} caractères",
    "validation.invalidFormat": "{field} a un format invalide",
    "validation.invalidId": "Format d'identifiant invalide",
    "validation.invalidDate": "Format de date invalide",
    "validation.invalidStatus": "Valeur de statut invalide",
    "validation.invalidDocumentType": "Type de document invalide",
    "validation.invalidDocumentSection": "Section de document invalide",
    "validation.fileTooLarge": "Le fichier est trop volumineux (max {max})",
    "validation.unsupportedFormat": "Format de fichier non supporté",
    "validation.unsupportedMimeType": "Type de fichier non supporté",

    // Network errors
    "network.timeout":
      "La requête a expiré. Vérifiez votre connexion et réessayez.",
    "network.offline":
      "Vous semblez être hors ligne. Vérifiez votre connexion.",
    "network.serverError": "Erreur serveur. Veuillez réessayer plus tard.",
    "network.connectionFailed": "Impossible de se connecter au serveur",

    // Authentication
    "auth.checking": "Vérification de l'authentification...",
    "auth.loginRequired": "Connexion requise",
    "auth.redirecting": "Redirection vers Spectral Cloud Portal...",
    "auth.loginWithPortal": "Se connecter via Portal",

    // Navigator - Bibliography Tab
    "notes.navTabBibliography": "Bibliographie",

    // Bibliography Panel
    "bibliography.notConfigured": "Zotero n'est pas configuré",
    "bibliography.configureInPortal": "Configurer dans Portal",
    "bibliography.loading": "Chargement de Zotero...",
    "bibliography.initializing": "Initialisation...",
    "bibliography.retry": "Réessayer",
    "bibliography.myLibrary": "Ma bibliothèque",
    "bibliography.collections": "Collections",
    "bibliography.noCollections": "Aucune collection",
    "bibliography.items": "Items",
    "bibliography.noItems": "Aucun item dans cette collection",
    "bibliography.untitled": "Sans titre",
    "bibliography.doubleClickToInsert": "Double-cliquer pour insérer la citation",
    "bibliography.tabLocal": "Local",
    "bibliography.tabZotero": "Zotero",
    "bibliography.localEmpty": "Aucune référence locale",
    "bibliography.localEmptyHint": "Insérez des citations depuis Zotero pour les sauvegarder localement",
    "bibliography.lastSync": "Dernière synchro",
    "bibliography.saveCollection": "Sauvegarder la collection",
    "bibliography.syncingMissing": "Synchronisation des citations...",
  },
};

// ============================================================================
// LANGUAGE STORE
// ============================================================================

/**
 * Create the language store with persistence
 */
function createLanguageStore(): LanguageStore {
  const initialLang: LanguageCode =
    (typeof localStorage !== "undefined" &&
      (localStorage.getItem("preparo-language") as LanguageCode)) ||
    "en";

  const { subscribe, set, update } = writable<LanguageCode>(initialLang);

  return {
    subscribe,
    set: (lang: LanguageCode) => {
      if (LANGUAGES[lang]) {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("preparo-language", lang);
        }
        set(lang);
      }
    },
    toggle: () => {
      update((currentLang) => {
        const newLang: LanguageCode = currentLang === "en" ? "fr" : "en";
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("preparo-language", newLang);
        }
        return newLang;
      });
    },
  };
}

export const currentLanguage: LanguageStore = createLanguageStore();

// ============================================================================
// TRANSLATION FUNCTIONS
// ============================================================================

/**
 * Translate a key with optional parameters
 */
export function t(key: TranslationKey, params: TranslationParams = {}): string {
  let lang: LanguageCode = "en";
  currentLanguage.subscribe((value) => {
    lang = value;
  })();

  let text = translations[lang]?.[key] || translations["en"]?.[key] || key;

  // Handle plural forms: "1 doc | 2 docs" format
  if (text.includes(" | ") && params.count !== undefined) {
    const [singular, plural] = text.split(" | ");
    text = params.count === 1 ? singular : plural;
  }

  // Replace parameters: {param} -> value
  Object.entries(params).forEach(([param, value]) => {
    if (value !== undefined) {
      text = text.replace(new RegExp(`\\{${param}\\}`, "g"), String(value));
    }
  });

  return text;
}

/**
 * Derived store for reactive translations
 */
export const i18n: Readable<TranslationFunction> = derived(
  currentLanguage,
  ($lang) => {
    return (key: TranslationKey, params: TranslationParams = {}): string => {
      let text = translations[$lang]?.[key] || translations["en"]?.[key] || key;

      // Handle plural forms
      if (text.includes(" | ") && params.count !== undefined) {
        const [singular, plural] = text.split(" | ");
        text = params.count === 1 ? singular : plural;
      }

      // Replace parameters
      Object.entries(params).forEach(([param, value]) => {
        if (value !== undefined) {
          text = text.replace(new RegExp(`\\{${param}\\}`, "g"), String(value));
        }
      });

      return text;
    };
  },
);

/**
 * Current language info derived store
 */
export const currentLanguageInfo: Readable<LanguageInfo> = derived(
  currentLanguage,
  ($lang) => LANGUAGES[$lang],
);
